/**
 * Notification bell component with unseen count badge
 * Follows container/presentation pattern
 *
 * Uses Facebook-style two-tier notification system:
 * - Badge shows unseen count (clears when panel opens)
 * - Notifications remain unread (highlighted) until clicked
 */
import { Bell, BellRing } from 'lucide-react';

import { useState } from 'react';

import { useUnseenNotificationCount } from '@/hooks/queries/useNotifications';

import NotificationDropdown from './NotificationDropdown';

/**
 * Container component for notification bell with state management
 */
export default function NotificationBell() {
  const [isOpen, setIsOpen] = useState(false);
  const unseenCount = useUnseenNotificationCount();

  return (
    <>
      <NotificationBellPresentation
        unseenCount={unseenCount}
        isOpen={isOpen}
        onClick={() => setIsOpen(!isOpen)}
      />
      {isOpen && <NotificationDropdown onClose={() => setIsOpen(false)} />}
    </>
  );
}

/**
 * Presentation component for notification bell UI
 */
interface NotificationBellPresentationProps {
  unseenCount: number;
  isOpen: boolean;
  onClick: () => void;
}

function NotificationBellPresentation({
  unseenCount,
  isOpen,
  onClick,
}: NotificationBellPresentationProps) {
  const hasUnseen = unseenCount > 0;
  const Icon = hasUnseen ? BellRing : Bell;

  return (
    <button
      onClick={onClick}
      className="relative rounded-full p-2 menu-btn hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300"
      aria-label={`Notifications${hasUnseen ? ` (${unseenCount} new)` : ''}`}
      data-testid="notification-bell"
      data-state={isOpen ? 'open' : 'closed'}
    >
      <Icon
        className={`h-6 w-6 ${hasUnseen ? 'text-primary-600 dark:text-primary-400' : ''}`}
      />

      {/* Unseen count badge - clears when panel opens */}
      {hasUnseen && (
        <span
          className="absolute -right-1 -top-1 flex min-h-5 min-w-5 px-1 py-0.5 items-center justify-center rounded-full bg-red-500 text-[11px] font-bold text-white"
          data-testid="notification-badge"
        >
          {unseenCount > 99 ? '99+' : unseenCount}
        </span>
      )}
    </button>
  );
}
